// frontend/src/pages/PrivacyPolicy.js
import React from 'react';
import { Link } from 'react-router-dom';
import { 
  ArrowLeft, 
  User, 
  Briefcase, 
  CreditCard, 
  Heart, 
  Users, 
  FileText,
  Lock,
  Clock,
  Mail,
  CheckCircle,
  AlertCircle,
  CalendarDays
} from 'lucide-react';
import DashboardLayout from '../components/Layout/DashboardLayout';
import { lastUpdated } from '../config/privacyPolicyMetadata';

const Section = ({ icon: Icon, title, children, iconColor = 'text-blue-600', iconBg = 'bg-blue-100' }) => (
  <div className="card">
    <div className="card-header">
      <div className="flex items-center gap-3">
        <div className={`w-10 h-10 rounded-full ${iconBg} flex items-center justify-center flex-shrink-0`}>
          <Icon className={`w-5 h-5 ${iconColor}`} />
        </div>
        <h2 className="text-subsection-header">{title}</h2>
      </div>
    </div>
    <div className="card-content">
      {children}
    </div>
  </div>
);

const DataCategory = ({ icon: Icon, title, items, color = 'blue' }) => {
  const colorClasses = {
    blue: { bg: 'bg-blue-50', text: 'text-blue-600', border: 'border-blue-200', bullet: 'bg-blue-600' },
    green: { bg: 'bg-green-50', text: 'text-green-600', border: 'border-green-200', bullet: 'bg-green-600' },
    purple: { bg: 'bg-purple-50', text: 'text-purple-600', border: 'border-purple-200', bullet: 'bg-purple-600' },
    amber: { bg: 'bg-amber-50', text: 'text-amber-600', border: 'border-amber-200', bullet: 'bg-amber-600' },
    rose: { bg: 'bg-rose-50', text: 'text-rose-600', border: 'border-rose-200', bullet: 'bg-rose-600' },
    indigo: { bg: 'bg-indigo-50', text: 'text-indigo-600', border: 'border-indigo-200', bullet: 'bg-indigo-600' },
  };
  const colors = colorClasses[color] || colorClasses.blue;

  return (
    <div className={`${colors.bg} rounded-xl p-4 border ${colors.border}`}>
      <div className="flex items-center gap-2 mb-3">
        <Icon className={`w-5 h-5 ${colors.text}`} />
        <h3 className="font-semibold text-gray-900">{title}</h3>
      </div>
      <ul className="space-y-1.5">
        {items.map((item, idx) => (
          <li key={idx} className="text-sm text-gray-700 flex items-start gap-2">
            <span className={`w-2 h-2 rounded-full ${colors.bullet} mt-1.5 flex-shrink-0`}></span>
            {item}
          </li>
        ))}
      </ul>
    </div>
  );
};

const PrivacyPolicy = () => {
  // lastUpdated is imported from auto-generated metadata file
  // It's automatically updated when PrivacyPolicy.js content changes

  return (
    <DashboardLayout>
      <div className="space-y-6 md:space-y-8 bg-gray-50 min-h-screen">
        {/* Page Header */}
        <div className="bg-white rounded-2xl shadow-lg p-4 sm:p-6 lg:p-8">
          <div className="flex items-center justify-between mb-4">
            <Link 
              to="/self-service/handbook" 
              className="inline-flex items-center text-sm text-gray-600 hover:text-blue-600 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 rounded"
            >
              <ArrowLeft className="w-4 h-4 mr-1" />
              Back to Employee Handbook & Policies
            </Link>
            <span className="text-sm text-gray-500">Last updated: {lastUpdated}</span>
          </div>
          <div>
            <h1 className="text-subsection-header">Privacy Policy</h1>
            <p className="text-body mt-1">Personal Data Protection Act (PDPA) Compliance</p>
          </div>
        </div>

        {/* Content */}
        <div className="space-y-6">
            {/* Introduction */}
            <Section icon={FileText} title="Introduction" iconColor="text-gray-600" iconBg="bg-gray-100">
              <p className="text-gray-700 leading-relaxed">
                This Privacy Policy explains how we collect, use, disclose, and protect your personal data 
                in accordance with the <strong>Singapore Personal Data Protection Act 2012 (PDPA)</strong>. 
                As your employer, we are committed to safeguarding your personal information and ensuring 
                transparency in our data practices.
              </p>
              <p className="text-gray-700 leading-relaxed mt-4">
                By using this HR Portal, you acknowledge that you have read and understood this Privacy Policy 
                and consent to the collection, use, and disclosure of your personal data as described herein.
              </p>
            </Section>

            {/* Data Categories */}
            <Section icon={User} title="Personal Data We Collect" iconColor="text-blue-600" iconBg="bg-blue-100">
              <p className="text-gray-700 mb-6">
                We collect and process the following categories of personal data for employment administration purposes:
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <DataCategory 
                  icon={User} 
                  title="Personal Identification" 
                  color="blue"
                  items={[
                    'Full name, date of birth, gender',
                    'NRIC/FIN number, passport details',
                    'Work permit/employment pass information',
                    'Photograph for identification purposes',
                    'Nationality and citizenship status'
                  ]}
                />
                <DataCategory 
                  icon={Mail} 
                  title="Contact Information" 
                  color="green"
                  items={[
                    'Residential address and postal code',
                    'Personal and work email addresses',
                    'Mobile and home phone numbers',
                    'Emergency contact information (name, phone, email, relationship of designated contacts)'
                  ]}
                />
                <DataCategory 
                  icon={Briefcase} 
                  title="Employment Records" 
                  color="purple"
                  items={[
                    'Employment start date and history',
                    'Job title, department, reporting structure',
                    'Work schedule and attendance records',
                    'Leave applications and balances',
                    'Performance and training records'
                  ]}
                />
                <DataCategory 
                  icon={CreditCard} 
                  title="Financial Information" 
                  color="amber"
                  items={[
                    'Bank account details for salary payment',
                    'Salary and compensation information',
                    'CPF contribution records',
                    'Tax-related information (IR8A)',
                    'Allowances and deductions'
                  ]}
                />
                <DataCategory 
                  icon={Heart} 
                  title="Medical & Leave Data" 
                  color="rose"
                  items={[
                    'Medical certificates for sick leave',
                    'Hospitalisation leave records',
                    'Maternity/paternity leave eligibility',
                    'Blood type (for emergency purposes)'
                  ]}
                />
                <DataCategory 
                  icon={Users} 
                  title="Family & Dependents" 
                  color="indigo"
                  items={[
                    'Spouse and dependent information',
                    'Children details (for childcare leave)',
                    'Marriage certificate information',
                    'Birth certificates of children'
                  ]}
                />
              </div>
            </Section>

            {/* Purposes */}
            <Section icon={CheckCircle} title="Purpose of Data Collection" iconColor="text-green-600" iconBg="bg-green-100">
              <p className="text-gray-700 mb-4">
                Your personal data is collected and used for the following purposes:
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {[
                  { title: 'Employment Administration', desc: 'Managing your employment relationship, including onboarding, HR records maintenance, and offboarding processes.' },
                  { title: 'Payroll Processing', desc: 'Processing salary payments, CPF contributions, and statutory deductions.' },
                  { title: 'Statutory Compliance', desc: 'Fulfilling legal obligations to government agencies including MOM, CPF Board, and IRAS.' },
                  { title: 'Leave Management', desc: 'Administering leave entitlements, applications, and balances in accordance with the Employment Act.' },
                  { title: 'Workplace Safety', desc: 'Emergency contact management and workplace safety compliance. Note: When providing emergency contact information, you confirm that you have obtained consent from your designated emergency contacts to store their personal information for emergency purposes.' },
                  { title: 'Benefits Administration', desc: 'Managing insurance, medical benefits, and other employee welfare programs.' },
                ].map((item, idx) => (
                  <div key={idx} className="flex items-start gap-3 p-3 bg-white rounded-lg border border-gray-100">
                    <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                    <div>
                      <h4 className="font-medium text-gray-900">{item.title}</h4>
                      <p className="text-sm text-gray-600">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </Section>

            {/* Data Sharing & Disclosure */}
            <Section icon={Users} title="Data Sharing & Disclosure" iconColor="text-indigo-600" iconBg="bg-indigo-100">
              <p className="text-gray-700 mb-4">
                To facilitate workplace coordination and planning, we share limited leave calendar information with your colleagues:
              </p>
              <div className="bg-indigo-50 rounded-xl p-5 border border-indigo-200 mb-4">
                <h4 className="font-semibold text-gray-900 mb-3 flex items-center">
                  <CalendarDays className="w-5 h-5 mr-2 text-indigo-600" />
                  Leave Calendar Sharing
                </h4>
                <p className="text-sm text-gray-700 mb-3">
                  When you provide general consent for data processing, you also consent to sharing the following information in department and company-wide leave calendars:
                </p>
                <ul className="space-y-2 text-sm text-gray-700 mb-4">
                  <li className="flex items-start gap-2">
                    <span className="text-indigo-600 mt-1">•</span>
                    <span><strong>Employee name and department</strong> - Essential for identifying who is away</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-indigo-600 mt-1">•</span>
                    <span><strong>Leave dates</strong> - Start and end dates for planning meetings and project deadlines</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-indigo-600 mt-1">•</span>
                    <span><strong>Time blocks (AM/PM)</strong> - For half-day leaves, indicating availability</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-indigo-600 mt-1">•</span>
                    <span><strong>Generic leave type labels</strong> - Privacy-protected categories (Out of Office, Unavailable, Extended Leave, Work From Home) instead of specific leave types</span>
                  </li>
                </ul>
                <div className="bg-white rounded-lg p-4 border border-indigo-100">
                  <p className="text-xs text-gray-600">
                    <strong>Privacy Protection:</strong> Sensitive leave types (maternity, medical, compassionate) are mapped to generic labels to protect your privacy while still providing useful information to colleagues for planning purposes.
                  </p>
                </div>
              </div>
              <div className="bg-amber-50 rounded-lg p-4 border border-amber-200">
                <p className="text-sm text-amber-800">
                  <strong>Withdrawal of Consent:</strong> You may withdraw your consent for calendar sharing at any time by contacting HR. Note that withdrawal will affect your visibility in department and company calendars.
                </p>
              </div>
            </Section>

            {/* Legal Basis */}
            <Section icon={FileText} title="Legal Basis for Processing" iconColor="text-purple-600" iconBg="bg-purple-100">
              <p className="text-gray-700 mb-4">
                We process your personal data based on the following legal grounds under the PDPA:
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="bg-white p-4 rounded-lg border border-gray-100">
                  <h4 className="font-semibold text-gray-900 mb-2">Consent</h4>
                  <p className="text-sm text-gray-600">
                    Your explicit consent provided through this HR Portal for data processing activities.
                  </p>
                </div>
                <div className="bg-white p-4 rounded-lg border border-gray-100">
                  <h4 className="font-semibold text-gray-900 mb-2">Contractual Necessity</h4>
                  <p className="text-sm text-gray-600">
                    Processing necessary for the performance of your employment contract.
                  </p>
                </div>
                <div className="bg-white p-4 rounded-lg border border-gray-100">
                  <h4 className="font-semibold text-gray-900 mb-2">Legal Obligation</h4>
                  <p className="text-sm text-gray-600">
                    Compliance with statutory requirements (Employment Act, CPF Act, Income Tax Act).
                  </p>
                </div>
                <div className="bg-white p-4 rounded-lg border border-gray-100">
                  <h4 className="font-semibold text-gray-900 mb-2">Legitimate Interest</h4>
                  <p className="text-sm text-gray-600">
                    Business operations, workforce planning, and organizational administration.
                  </p>
                </div>
              </div>
            </Section>

            {/* Data Protection */}
            <Section icon={Lock} title="Data Protection Measures" iconColor="text-amber-600" iconBg="bg-amber-100">
              <p className="text-gray-700 mb-4">
                We implement robust security measures to protect your personal data:
              </p>
              <ul className="space-y-2">
                {[
                  'All sensitive data is encrypted at rest and in transit',
                  'Role-based access control limits data access to authorized personnel only',
                  'Comprehensive audit logging tracks all data access and modifications',
                  'Regular security assessments and vulnerability testing',
                  'Secure backup and disaster recovery procedures',
                  'Staff training on data protection and handling procedures'
                ].map((item, idx) => (
                  <li key={idx} className="flex items-start gap-2 text-gray-700">
                    <Lock className="w-4 h-4 text-amber-500 mt-1 flex-shrink-0" />
                    <span className="text-sm">{item}</span>
                  </li>
                ))}
              </ul>
            </Section>

            {/* Data Retention */}
            <Section icon={Clock} title="Data Retention" iconColor="text-indigo-600" iconBg="bg-indigo-100">
              <p className="text-gray-700 mb-4">
                We retain your personal data for the following periods:
              </p>
              <div className="bg-white rounded-lg border border-gray-100 overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left font-semibold text-gray-900">Data Type</th>
                      <th className="px-4 py-3 text-left font-semibold text-gray-900">Retention Period</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    <tr>
                      <td className="px-4 py-3 text-gray-700">Employment records</td>
                      <td className="px-4 py-3 text-gray-600">2 years during employment, 1 year after leaving</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-gray-700">Salary records</td>
                      <td className="px-4 py-3 text-gray-600">2 years during employment, 1 year after leaving</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-gray-700">CPF contribution records</td>
                      <td className="px-4 py-3 text-gray-600">7 years (CPF Board)</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-gray-700">Tax records (IR8A)</td>
                      <td className="px-4 py-3 text-gray-600">5 years (IRAS)</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-gray-700">Leave and attendance records</td>
                      <td className="px-4 py-3 text-gray-600">2 years during employment, 1 year after leaving</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-gray-700">Medical certificates</td>
                      <td className="px-4 py-3 text-gray-600">1 year after relevant leave period</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <p className="text-sm text-gray-600 mt-4">
                After the retention period, your data will be securely deleted or anonymized.
              </p>
            </Section>

            {/* Your Rights */}
            <Section icon={User} title="Your Rights Under PDPA" iconColor="text-rose-600" iconBg="bg-rose-100">
              <p className="text-gray-700 mb-4">
                As an employee, you have the following rights regarding your personal data:
              </p>
              <div className="space-y-4">
                <div className="flex items-start gap-3 p-4 bg-white rounded-lg border border-gray-100">
                  <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <span className="text-blue-600 font-bold text-sm">1</span>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900">Right of Access</h4>
                    <p className="text-sm text-gray-600 mt-1">
                      You can view all your personal data through the <Link to="/profile" className="text-blue-600 hover:underline font-medium">My Profile</Link> page in this HR Portal.
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3 p-4 bg-white rounded-lg border border-gray-100">
                  <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <span className="text-green-600 font-bold text-sm">2</span>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900">Right of Correction</h4>
                    <p className="text-sm text-gray-600 mt-1">
                      You can update and correct your personal information through the <Link to="/profile" className="text-blue-600 hover:underline font-medium">My Profile</Link> page. Some fields may require HR verification.
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3 p-4 bg-white rounded-lg border border-gray-100">
                  <div className="w-8 h-8 bg-amber-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <span className="text-amber-600 font-bold text-sm">3</span>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900">Right to Withdraw Consent</h4>
                    <p className="text-sm text-gray-600 mt-1">
                      You may withdraw your consent at any time by contacting HR. Note that withdrawal may affect our ability to fulfill employment obligations.
                    </p>
                  </div>
                </div>
              </div>
            </Section>

            {/* Digital Name Card */}
            <Section icon={CreditCard} title="Digital Name Card (Opt-In)" iconColor="text-indigo-600" iconBg="bg-indigo-100">
              <p className="text-gray-700 mb-4">
                The Digital Name Card feature allows you to create a shareable contact card with a unique URL and QR code. This feature is <strong>entirely voluntary</strong> and requires separate, field-level consent.
              </p>

              <div className="space-y-4">
                <div className="bg-white p-4 rounded-lg border border-gray-100">
                  <h4 className="font-semibold text-gray-900 mb-2">What You Control</h4>
                  <ul className="space-y-2 text-sm text-gray-600">
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                      <span>You choose exactly which fields appear on your card (designation, department, work email, work phone, extension, personal mobile, LinkedIn).</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                      <span>Personal mobile number sharing requires explicit additional consent, as it is personal data under the PDPA.</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                      <span>You can pause your card (temporarily hide it) or fully revoke consent at any time from your Profile page.</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                      <span>Revoking consent permanently deletes your card and invalidates the public URL.</span>
                    </li>
                  </ul>
                </div>

                <div className="bg-white p-4 rounded-lg border border-gray-100">
                  <h4 className="font-semibold text-gray-900 mb-2">Privacy Protections</h4>
                  <ul className="space-y-2 text-sm text-gray-600">
                    <li className="flex items-start gap-2">
                      <Lock className="w-4 h-4 text-blue-500 mt-0.5 flex-shrink-0" />
                      <span>Your card URL is not indexed by search engines.</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Lock className="w-4 h-4 text-blue-500 mt-0.5 flex-shrink-0" />
                      <span>The view counter is non-identifiable — no visitor personal data is collected or stored.</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Lock className="w-4 h-4 text-blue-500 mt-0.5 flex-shrink-0" />
                      <span>If your employment ends, your card is automatically deactivated and the public link is permanently invalidated.</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Lock className="w-4 h-4 text-blue-500 mt-0.5 flex-shrink-0" />
                      <span>NRIC/FIN, personal email, home address, salary, and other sensitive data are never included on the card regardless of consent.</span>
                    </li>
                  </ul>
                </div>
              </div>
            </Section>

            {/* Contact */}
            <Section icon={Mail} title="Contact Us" iconColor="text-teal-600" iconBg="bg-teal-100">
              <p className="text-gray-700 mb-4">
                If you have any questions about this Privacy Policy or wish to exercise your data protection rights, please contact:
              </p>
              <div className="bg-white p-4 rounded-lg border border-gray-100">
                <h4 className="font-semibold text-gray-900 mb-2">Human Resources Department</h4>
                <p className="text-sm text-gray-600">Data Protection Officer</p>
                <p className="text-sm text-gray-600 mt-2">
                  Email: <a href="mailto:dpo@winner-e.sg" className="text-blue-600 hover:underline">dpo@winner-e.sg</a>
                </p>
              </div>
              <div className="mt-4 p-4 bg-amber-50 rounded-lg border border-amber-200">
                <div className="flex items-start gap-2">
                  <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
                  <p className="text-sm text-amber-800">
                    We will respond to your request within <strong>30 days</strong> as required by the PDPA.
                  </p>
                </div>
              </div>
            </Section>

            {/* Footer */}
            <div className="text-center py-4 text-sm text-gray-500">
              <p>This Privacy Policy is effective as of {lastUpdated} and applies to all employees.</p>
              <p className="mt-1">We may update this policy from time to time. You will be notified of any significant changes.</p>
            </div>
          </div>
        </div>
    </DashboardLayout>
  );
};

export default PrivacyPolicy;

